(function () {
  'use strict';

  const root = window.CarturioRoot || window.Shopify?.routes?.root || '/';
  const predictiveEndpoint = window.CarturioPredictiveSearch || `${root}search/suggest`;
  const WISHLIST_KEY = 'carturio_wishlist_v4';
  const RECENT_KEY = 'carturio_recent_v4';
  const MAX_ITEMS = 100;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Some mobile/in-app browsers block localStorage. Keep session state in memory
  // whenever persistence is unavailable instead of making wishlist taps look broken.
  const memoryStore = new Map();
  const storageUnavailable = new Set();
  const read = (key, fallback) => {
    if (storageUnavailable.has(key)) return memoryStore.has(key) ? memoryStore.get(key) : fallback;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw !== null) {
        const value = JSON.parse(raw);
        memoryStore.set(key, value);
        return value;
      }
      return memoryStore.has(key) ? memoryStore.get(key) : fallback;
    } catch (_) {
      storageUnavailable.add(key);
      return memoryStore.has(key) ? memoryStore.get(key) : fallback;
    }
  };
  const write = (key, value) => {
    memoryStore.set(key, value);
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
      storageUnavailable.delete(key);
    } catch (_) {
      storageUnavailable.add(key);
    }
  };
  const wishlist = () => {
    const current = read(WISHLIST_KEY, null);
    if (Array.isArray(current) && current.length) return current.filter((item) => typeof item === 'string' && item).slice(0, MAX_ITEMS);
    // Bring forward saved items from older Carturio scripts if V11 has no saved list.
    for (const legacyKey of ['carturio_wishlist_v3', 'carturio_wishlist_v2']) {
      const legacy = read(legacyKey, []);
      if (Array.isArray(legacy) && legacy.length) {
        const migrated = [...new Set(legacy.filter((item) => typeof item === 'string' && item))].slice(0, MAX_ITEMS);
        write(WISHLIST_KEY, migrated);
        return migrated;
      }
    }
    return Array.isArray(current) ? current : [];
  };
  const recent = () => read(RECENT_KEY, []);
  const escapeHtml = (value) => String(value ?? '').replace(/[&<>'"]/g, (char) => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', "'":'&#39;', '"':'&quot;' }[char]));

  // V11 security hardening: constrain generated links to this storefront and images to HTTPS.
  const safeStoreUrl = (value, fallback = '/') => {
    const safeFallback = (() => {
      try {
        const u = new URL(String(fallback || '/'), window.location.origin);
        return u.origin === window.location.origin && ['https:', 'http:'].includes(u.protocol) ? u.pathname + u.search + u.hash : '/';
      } catch (_) { return '/'; }
    })();
    try {
      const u = new URL(String(value || ''), window.location.origin);
      if (!['https:', 'http:'].includes(u.protocol) || u.origin !== window.location.origin) return safeFallback;
      return u.pathname + u.search + u.hash;
    } catch (_) { return safeFallback; }
  };
  const safeProductUrl = (value, handle) => safeStoreUrl(value, root + 'products/' + encodeURIComponent(handle || ''));
  const safeImageUrl = (value) => {
    try {
      const u = new URL(String(value || ''), window.location.origin);
      if (u.protocol !== 'https:' && u.origin !== window.location.origin) return '';
      if (u.protocol !== 'https:' && u.protocol !== 'http:') return '';
      return u.href;
    } catch (_) { return ''; }
  };
  const money = (cents) => {
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', { style: 'currency', currency: window.CarturioCurrency || 'INR', maximumFractionDigits: 2 }).format(Number(cents || 0) / 100); }
    catch (_) { return `INR ${(Number(cents || 0) / 100).toFixed(2)}`; }
  };

  const toast = (message) => {
    let el = document.querySelector('[data-carturio-toast]');
    if (!el) { el = document.createElement('div'); el.className = 'carturio-toast'; el.dataset.carturioToast = ''; document.body.appendChild(el); }
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el._timer);
    el._timer = setTimeout(() => el.classList.remove('is-visible'), 2600);
  };

  const syncCounts = async () => {
    const saved = wishlist();
    document.querySelectorAll('[data-carturio-wishlist-count]').forEach((el) => { el.textContent = saved.length; el.hidden = saved.length === 0; });
    document.querySelectorAll('[data-carturio-wishlist]').forEach((el) => {
      const active = saved.includes(el.getAttribute('data-carturio-wishlist'));
      el.classList.toggle('is-active', active); el.setAttribute('aria-pressed', active ? 'true' : 'false'); el.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
    });
    try {
      const response = await fetch(`${root}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-carturio-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.classList.toggle('is-zero', cart.item_count === 0); });
    } catch (_) {}
  };

  const fetchProduct = async (handle) => {
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Product unavailable');
    return response.json();
  };

  const buildWishlistCards = async (handles) => {
    const cards = [];
    for (const handle of handles.slice(0, 12)) {
      try {
        const product = await fetchProduct(handle);
        const image = safeImageUrl(product.featured_image || product.images?.[0] || '');
        const productUrl = escapeHtml(safeProductUrl(product.url, product.handle || handle));
        cards.push(`<article class="carturio-wishlist-page-card"><a href="${productUrl}" class="carturio-wishlist-page-card__image">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : ''}</a><div class="carturio-wishlist-page-card__body"><a href="${productUrl}">${escapeHtml(product.title)}</a><strong>${money(product.price)}</strong><div><a class="carturio-mini-button" href="${productUrl}">View product</a><button type="button" class="carturio-mini-button carturio-mini-button--muted" data-carturio-wishlist="${escapeHtml(product.handle)}">Remove</button></div></div></article>`);
      } catch (_) {}
    }
    return cards;
  };

  const renderWishlistDrawer = async () => {
    const host = document.querySelector('[data-carturio-wishlist-list]');
    const empty = document.querySelector('[data-carturio-wishlist-empty]');
    if (!host || !empty) return;
    const handles = wishlist().slice(0, 12);
    if (!handles.length) { host.innerHTML = ''; empty.hidden = false; return; }
    empty.hidden = true; host.innerHTML = '<div class="carturio-drawer-loading">Loading saved items...</div>';
    const cards = await buildWishlistCards(handles);
    host.innerHTML = cards.join('') || '<div class="carturio-drawer-loading">Saved products are no longer available.</div>';
    syncCounts();
  };

  const toggleWishlist = (handle) => {
    const list = wishlist(); const index = list.indexOf(handle);
    if (index >= 0) { list.splice(index, 1); toast('Removed from wishlist'); }
    else { list.unshift(handle); toast('Saved to wishlist'); }
    write(WISHLIST_KEY, list.slice(0, MAX_ITEMS));
    syncCounts(); renderWishlistDrawer(); renderWishlistPage();
  };

  const openWishlist = () => {
    const drawer = document.querySelector('[data-carturio-wishlist-drawer]');
    if (!drawer) return;
    drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden', 'false'); document.body.classList.add('carturio-drawer-open'); renderWishlistDrawer();
    drawer.querySelector('.carturio-drawer-close')?.focus();
  };

  const closeWishlist = () => {
    const drawer = document.querySelector('[data-carturio-wishlist-drawer]');
    if (!drawer) return;
    drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true'); document.body.classList.remove('carturio-drawer-open');
  };

  const quickAdd = async (form) => {
    const button = form.querySelector('button[type="submit"]');
    const original = button?.innerHTML || '';
    if (button) { button.disabled = true; button.innerHTML = '<span>Adding...</span>'; }
    try {
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', body: new FormData(form), credentials: 'same-origin', headers: { Accept: 'application/json' } });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Unable to add to cart');
      await syncCounts();
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.open) drawer.open();
      else if (drawer?.querySelector('[data-drawer]')) document.body.dispatchEvent(new CustomEvent('cart:refresh'));
      toast('Added to cart');
    } catch (error) { toast(error.message || 'Unable to add to cart'); }
    finally { if (button) { button.disabled = false; button.innerHTML = original; } }
  };

  const alternateSearchTerm = (term) => {
    const spellingPairs = [
      [/\bgray\b/i, 'grey'],
      [/\bgrey\b/i, 'gray'],
      [/\bcolour\b/i, 'color'],
      [/\bcolor\b/i, 'colour'],
      [/\bmulticolour\b/i, 'multicolor'],
      [/\bmulticolor\b/i, 'multicolour']
    ];
    for (const [pattern, replacement] of spellingPairs) {
      if (pattern.test(term)) {
        const alternative = term.replace(pattern, replacement);
        if (alternative.toLowerCase() !== term.toLowerCase()) return alternative;
      }
    }
    return '';
  };

  const formatSearchPrice = (amount) => {
    const value = Number(amount);
    if (!Number.isFinite(value)) return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency',
        currency: window.CarturioCurrency || 'INR',
        maximumFractionDigits: 2
      }).format(value);
    } catch (_) {
      return (window.CarturioCurrency || 'INR') + ' ' + value.toFixed(2);
    }
  };

  const setupSearch = () => {
    const form = document.querySelector('[data-carturio-search-form]');
    const input = document.querySelector('[data-carturio-search-input]');
    const box = document.querySelector('[data-carturio-search-results]');
    if (!form || !input || !box) return;

    box.setAttribute('role', 'listbox');
    box.setAttribute('aria-label', 'Search suggestions');
    let controller;
    let timer;
    let activeOptionIndex = -1;
    let requestVersion = 0;

    const close = () => {
      box.hidden = true;
      box.removeAttribute('aria-busy');
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeOptionIndex = -1;
    };

    const getOptions = () => Array.from(box.querySelectorAll('[role="option"]'));

    const setActiveOption = (index, shouldScroll) => {
      const options = getOptions();
      if (!options.length) return;
      activeOptionIndex = (index + options.length) % options.length;
      options.forEach((option, optionIndex) => {
        const active = optionIndex === activeOptionIndex;
        option.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) {
          input.setAttribute('aria-activedescendant', option.id);
          if (shouldScroll) option.scrollIntoView({ block: 'nearest' });
        }
      });
    };

    const makeOption = (href, className, content) => {
      const id = 'CarturioSearchOption-' + getOptions().length;
      return '<a id="' + id + '" role="option" aria-selected="false" class="' + className + '" href="' +
        escapeHtml(safeStoreUrl(href || form.action, form.action)) + '">' + content + '</a>';
    };

    const makeGroup = (label, items) => {
      if (!items.length) return '';
      return '<div class="carturio-search__group" role="group" aria-label="' + escapeHtml(label) +
        '"><span>' + escapeHtml(label) + '</span>' + items.join('') + '</div>';
    };

    const fetchSuggestions = async (term, signal) => {
      const url = new URL(predictiveEndpoint, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', 'product,collection,page,query');
      url.searchParams.set('resources[limit]', '8');
      url.searchParams.set('resources[options][unavailable_products]', 'hide');
      url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,tag,body,variants.sku');
      const response = await fetch(url, {
        signal,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error('Search suggestions unavailable');
      return response.json();
    };

    const mergeResults = (responses) => {
      const merged = { products: [], collections: [], pages: [], queries: [] };
      const keys = Object.keys(merged);
      keys.forEach((key) => {
        const seen = new Set();
        responses.forEach((response) => {
          const resultList = response?.resources?.results?.[key] || [];
          resultList.forEach((item) => {
            const identity = item.handle || item.id || item.url || item.text || item.title;
            if (identity == null || seen.has(String(identity))) return;
            seen.add(String(identity));
            merged[key].push(item);
          });
        });
      });
      return merged;
    };

    const renderResults = (results, query) => {
      let optionCount = 0;
      const renderOption = (href, className, content) => {
        const id = 'CarturioSearchOption-' + optionCount++;
        return '<a id="' + id + '" role="option" aria-selected="false" class="' + className + '" href="' +
          escapeHtml(safeStoreUrl(href || form.action, form.action)) + '">' + content + '</a>';
      };
      const output = [];

      const products = (results.products || []).slice(0, 6).map((product) => {
        const rawImage = product.featured_image?.url || product.image?.url ||
          (typeof product.image === 'string' ? product.image : '');
        const imageUrl = safeImageUrl(rawImage);
        const image = imageUrl
          ? '<img src="' + escapeHtml(imageUrl) + '" alt="" loading="lazy" decoding="async">'
          : '<span class="carturio-search-result__blank" aria-hidden="true"></span>';
        const title = escapeHtml(product.title || 'View product');
        const meta = [];
        if (product.vendor) meta.push(escapeHtml(product.vendor));
        if (product.price_min != null && formatSearchPrice(product.price_min)) {
          meta.push(escapeHtml(formatSearchPrice(product.price_min)));
        }
        return renderOption(product.url, 'carturio-search-result', image +
          '<span class="carturio-search-result__copy"><strong>' + title + '</strong>' +
          (meta.length ? '<small>' + meta.join(' <span aria-hidden="true">·</span> ') + '</small>' : '') +
          '</span>');
      });
      if (products.length) output.push(makeGroup('Products', products));

      const collections = (results.collections || []).slice(0, 3).map((item) =>
        renderOption(item.url, 'carturio-search-text-result', escapeHtml(item.title || 'Collection')));
      if (collections.length) output.push(makeGroup('Collections', collections));

      const queries = (results.queries || []).slice(0, 3).map((item) => {
        const label = item.text || item.query || item.title || '';
        const href = item.url || (form.action + '?q=' + encodeURIComponent(label) + '&type=product%2Ccollection%2Cpage');
        if (!label) return '';
        return renderOption(href, 'carturio-search-text-result carturio-search-query-result',
          '<span aria-hidden="true" class="carturio-search-query-icon">↗</span>' + escapeHtml(label));
      }).filter(Boolean);
      if (queries.length) output.push(makeGroup('Search suggestions', queries));

      const pages = (results.pages || []).slice(0, 2).map((item) =>
        renderOption(item.url, 'carturio-search-text-result', escapeHtml(item.title || 'Page')));
      if (pages.length) output.push(makeGroup('Pages', pages));

      if (!output.length) {
        output.push('<div class="carturio-search__no-results"><strong>No close matches yet</strong>' +
          '<p>Try a colour with an item name, like “blue headphones” or “black bag”.</p></div>');
      }

      const allUrl = new URL(form.action, window.location.origin);
      allUrl.searchParams.set('q', query);
      allUrl.searchParams.set('type', 'product,collection,page');
      allUrl.searchParams.set('options[prefix]', 'last');
      const viewAll = renderOption(allUrl.toString(), 'carturio-search__view-all',
        'View all results for &ldquo;' + escapeHtml(query) + '&rdquo; <span aria-hidden="true">→</span>');
      output.push(viewAll);
      return output.join('');
    };

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const query = input.value.trim().replace(/\s+/g, ' ');
      const version = ++requestVersion;
      activeOptionIndex = -1;
      input.removeAttribute('aria-activedescendant');

      if (query.length < 2) {
        controller?.abort();
        close();
        return;
      }

      timer = setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        const signal = controller.signal;
        box.hidden = false;
        box.setAttribute('aria-busy', 'true');
        input.setAttribute('aria-expanded', 'true');
        box.innerHTML = '<div class="carturio-search__loading" role="status">Finding matching products&hellip;</div>';

        try {
          const alternate = alternateSearchTerm(query);
          const terms = alternate ? [query, alternate] : [query];
          const responses = await Promise.all(terms.map((term) => fetchSuggestions(term, signal)));
          if (signal.aborted || version !== requestVersion) return;
          box.innerHTML = renderResults(mergeResults(responses), query);
          box.hidden = false;
          input.setAttribute('aria-expanded', 'true');
        } catch (error) {
          if (error.name !== 'AbortError' && version === requestVersion) {
            box.innerHTML = '<div class="carturio-search__no-results"><strong>Search is temporarily unavailable</strong>' +
              '<p>Press Enter to search the full catalog.</p></div>';
            box.hidden = false;
            input.setAttribute('aria-expanded', 'true');
          }
        } finally {
          if (version === requestVersion) box.removeAttribute('aria-busy');
        }
      }, 160);
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !box.hidden) {
        event.preventDefault();
        close();
        return;
      }
      if (box.hidden) return;
      const options = getOptions();
      if (!options.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActiveOption(activeOptionIndex + 1, true);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActiveOption(activeOptionIndex < 0 ? options.length - 1 : activeOptionIndex - 1, true);
      } else if (event.key === 'Enter' && activeOptionIndex >= 0) {
        const active = options[activeOptionIndex];
        if (active?.href) {
          event.preventDefault();
          window.location.assign(active.href);
        }
      }
    });

    box.addEventListener('pointerover', (event) => {
      const option = event.target.closest('[role="option"]');
      if (!option) return;
      const options = getOptions();
      const index = options.indexOf(option);
      if (index >= 0) setActiveOption(index, false);
    });

    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) close();
    });
  };

  const setupRecent = async () => {
    const handle = document.documentElement.dataset.carturioProduct;
    if (handle) { const list = recent().filter((item) => item !== handle); list.unshift(handle); write(RECENT_KEY, list.slice(0, 12)); }
    const host = document.querySelector('[data-carturio-recent-host]');
    if (!host) return;
    const handles = recent().slice(0, 6);
    if (!handles.length) { host.closest('[data-carturio-recent-section]')?.setAttribute('hidden', ''); return; }
    const cards = [];
    for (const item of handles) {
      try {
        const product = await fetchProduct(item); const image = safeImageUrl(product.featured_image || product.images?.[0] || ''); const productUrl = escapeHtml(safeProductUrl(product.url, product.handle || item));
        cards.push(`<article class="carturio-product-card carturio-product-card--recent"><div class="carturio-product-card__media-wrap"><a href="${productUrl}" class="carturio-product-card__media">${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy" class="carturio-product-card__image">` : ''}</a><button type="button" class="carturio-icon-button carturio-wishlist-button" data-carturio-wishlist="${escapeHtml(product.handle || item)}" aria-label="Add to wishlist" aria-pressed="false"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.2 8.6c0 4.7-8.2 9.6-8.2 9.6S3.8 13.3 3.8 8.6A4.5 4.5 0 0 1 8.3 4c1.4 0 2.7.7 3.7 1.9C13 4.7 14.3 4 15.7 4a4.5 4.5 0 0 1 4.5 4.6Z" fill="none" stroke="currentColor" stroke-width="1.8"/></svg></button></div><div class="carturio-product-card__body"><p class="carturio-product-card__vendor">Recently viewed</p><h3 class="carturio-product-card__title"><a href="${productUrl}">${escapeHtml(product.title)}</a></h3><div class="carturio-price-row"><strong>${money(product.price)}</strong></div></div></article>`);
      } catch (_) {}
    }
    if (cards.length) host.innerHTML = `<div class="carturio-product-rail">${cards.join('')}</div>`;
    else host.closest('[data-carturio-recent-section]')?.setAttribute('hidden', '');
  };

  const renderWishlistPage = async () => {
    const host = document.querySelector('[data-carturio-wishlist-page-host]'); if (!host) return;
    const handles = wishlist().slice(0, 24);
    if (!handles.length) { host.innerHTML = '<div class="carturio-wishlist-page-empty"><strong>No saved items yet.</strong><p>Use the heart on any product to save it here.</p></div>'; return; }
    host.innerHTML = '<div class="carturio-drawer-loading">Loading saved items...</div>';
    const cards = await buildWishlistCards(handles);
    host.innerHTML = cards.length ? `<div class="carturio-wishlist-page-grid">${cards.join('')}</div>` : '<div class="carturio-drawer-loading">Saved products are no longer available.</div>';
  };

  const quickView = async (handle) => {
    const modal = document.querySelector('[data-carturio-quick-view-modal]');
    const body = modal?.querySelector('[data-carturio-quick-view-body]');
    if (!modal || !body) return;
    modal.hidden = false; document.body.classList.add('carturio-modal-open'); body.innerHTML = '<div class="carturio-drawer-loading">Loading product...</div>';
    try {
      const product = await fetchProduct(handle); const image = safeImageUrl(product.featured_image || product.images?.[0] || ''); const productUrl = escapeHtml(safeProductUrl(product.url, product.handle || handle));
      body.innerHTML = `<div class="carturio-quick-view-grid"><div class="carturio-quick-view-image">${image ? `<img src="${escapeHtml(image)}" alt="" loading="eager">` : ''}</div><div class="carturio-quick-view-copy"><p class="carturio-product-card__vendor">${escapeHtml(product.vendor || 'Carturio')}</p><h2>${escapeHtml(product.title)}</h2><button type="button" class="carturio-product-wishlist-inline carturio-quick-view__wishlist" data-carturio-wishlist="${escapeHtml(product.handle || handle)}" aria-label="Add to wishlist" aria-pressed="false"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.2 8.6c0 4.7-8.2 9.6-8.2 9.6S3.8 13.3 3.8 8.6A4.5 4.5 0 0 1 8.3 4c1.4 0 2.7.7 3.7 1.9C13 4.7 14.3 4 15.7 4a4.5 4.5 0 0 1 4.5 4.6Z" fill="none" stroke="currentColor" stroke-width="1.8"/></svg><span>Save to wishlist</span></button><div class="carturio-price-row"><strong>${money(product.price)}</strong></div><p>${escapeHtml((product.description || '').replace(/<[^>]*>/g, '').slice(0, 320))}</p><a class="carturio-button carturio-button--primary" href="${productUrl}">View product</a></div></div>`;
    } catch (_) { body.innerHTML = '<p>Unable to load this product right now.</p>'; }
  };
  const closeQuickView = () => { const modal = document.querySelector('[data-carturio-quick-view-modal]'); if (modal) modal.hidden = true; document.body.classList.remove('carturio-modal-open'); };

  const setupTracking = () => {
    document.querySelectorAll('[data-carturio-track-form]').forEach((form) => {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const input = form.querySelector('[name="tracking_number"]');
        const value = input?.value.trim();
        const template = form.dataset.trackingTemplate || '';
        if (!value) return;
        if (!template || !/\{tracking_number\}/i.test(template)) { toast('The store has not configured a carrier tracking URL yet.'); return; }
        const target = template.replace(/\{tracking_number\}/gi, encodeURIComponent(value));
        window.location.href = target;
      });
    });
  };

  const setupMobileMenu = () => {
    const button = document.querySelector('[data-carturio-mobile-toggle]');
    const panel = document.querySelector('[data-carturio-mobile-panel]');
    const header = document.querySelector('[data-carturio-header]');
    const mainHeader = document.querySelector('.carturio-main-header');
    if (!button || !panel || !header) return;

    const setPanelTop = () => {
      if (window.matchMedia('(max-width: 749px)').matches) {
        const anchor = mainHeader || header;
        document.documentElement.style.setProperty('--carturio-mobile-top', `${Math.ceil(anchor.getBoundingClientRect().bottom)}px`);
      } else {
        document.documentElement.style.removeProperty('--carturio-mobile-top');
      }
    };

    const close = (restoreFocus = false) => {
      panel.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('carturio-mobile-menu-open');
      const label = button.querySelector('span');
      if (label) label.textContent = 'Menu';
      if (restoreFocus) button.focus({ preventScroll: true });
    };

    const toggle = () => {
      const willOpen = panel.hidden;
      setPanelTop();
      panel.hidden = !willOpen;
      button.setAttribute('aria-expanded', String(willOpen));
      const label = button.querySelector('span');
      if (label) label.textContent = willOpen ? 'Close' : 'Menu';
      document.body.classList.toggle('carturio-mobile-menu-open', willOpen);
      if (willOpen) {
        const firstLink = panel.querySelector('a,button');
        firstLink?.focus({ preventScroll: true });
      }
    };

    setPanelTop();
    button.addEventListener('click', toggle);
    panel.addEventListener('click', (event) => {
      if (event.target.closest('a,button')) {
        close(false);
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) close(true);
    });
    window.addEventListener('resize', () => {
      setPanelTop();
      if (!window.matchMedia('(max-width: 749px)').matches) close(false);
    }, { passive: true });
  };

  const setupMotion = () => {
    const items = document.querySelectorAll('.carturio-reveal');
    if (!items.length) return;

    // Fail open: never let a missing animation API hide storefront content.
    const showAll = () => items.forEach((item) => item.classList.add('is-visible'));
    if (reducedMotion() || typeof window.IntersectionObserver !== 'function') {
      showAll();
      return;
    }

    const observer = new window.IntersectionObserver((entries, obs) => entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        obs.unobserve(entry.target);
      }
    }), { threshold: 0.08, rootMargin: '0px 0px -35px' });

    // Enable reveal transitions only when the browser supports the observer.
    document.documentElement.classList.add('carturio-motion-ready');
    items.forEach((item, index) => {
      item.style.setProperty('--carturio-delay', `${Math.min(index * 45, 260)}ms`);
      observer.observe(item);
    });

    const hero = document.querySelector('.carturio-hero');
    const heroMedia = hero?.querySelector('.carturio-hero__media');
    if (hero && heroMedia && !reducedMotion() && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      hero.addEventListener('pointermove', (event) => {
        const rect = hero.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - 0.5) * 2;
        const y = ((event.clientY - rect.top) / rect.height - 0.5) * 2;
        heroMedia.style.transform = `translate3d(${x * -8}px, ${y * -5}px, 0) scale(1.025)`;
      }, { passive: true });
      hero.addEventListener('pointerleave', () => { heroMedia.style.transform = ''; });
    }
  };

  const setupScrollProgress = () => {
    const bar = document.querySelector('[data-carturio-scroll-progress] span'); if (!bar) return;
    const update = () => { const max = document.documentElement.scrollHeight - window.innerHeight; bar.style.transform = `scaleX(${max > 0 ? window.scrollY / max : 0})`; };
    update(); window.addEventListener('scroll', update, { passive: true }); window.addEventListener('resize', update, { passive: true });
  };

  const setupProductRails = () => {
    const roleRank = { featured: 0, catalog: 1 };
    const rails = Array.from(document.querySelectorAll('[data-carturio-product-rail]')).map((element) => ({
      element,
      role: element.dataset.carturioRailRole || '',
      limit: Math.max(1, Number.parseInt(element.dataset.carturioRailLimit || '5', 10) || 5),
      cards: Array.from(element.querySelectorAll(':scope > .carturio-product-card'))
    })).filter((rail) => rail.cards.length);

    rails.sort((a, b) => (roleRank[a.role] ?? 2) - (roleRank[b.role] ?? 2));
    const usedHandles = new Set();

    rails.forEach((rail) => {
      const handleFor = (card) => card.querySelector('[data-carturio-wishlist]')?.getAttribute('data-carturio-wishlist') || '';
      const candidates = rail.cards.filter((card) => {
        const handle = handleFor(card);
        return handle && !usedHandles.has(handle);
      });
      const selected = candidates.slice(0, rail.limit);

      rail.cards.forEach((card) => { card.hidden = true; });
      selected.forEach((card) => {
        const handle = handleFor(card);
        if (handle) usedHandles.add(handle);
        card.hidden = false;
        rail.element.appendChild(card);
      });
      rail.cards.filter((card) => !selected.includes(card)).forEach((card) => {
        card.hidden = true;
        rail.element.appendChild(card);
      });
      rail.element.classList.add('is-ready');
    });
  };

  // Register product wishlist interaction before DOMContentLoaded and before
  // optional enhancements. Use touch pointer-up as the primary mobile path,
  // then suppress its compatibility click so one tap never toggles twice.
  const installWishlistInteractions = () => {
    const touchStarts = new Map();
    const skipCompatibilityClick = new WeakSet();
    const getElement = (target) => target instanceof Element ? target : target?.parentElement;
    const findWishlistButton = (target) => getElement(target)?.closest('[data-carturio-wishlist]');

    // Card title links can create a full-card hit area on some mobile layouts.
    // Resolve a tap by its coordinates when the browser reports the link beneath
    // the visible heart button as the event target.
    const findWishlistButtonAtEvent = (event) => {
      const direct = findWishlistButton(event?.target);
      if (direct) return direct;
      if (!event || (event.type === 'click' && event.detail === 0)) return null;

      let x = event.clientX;
      let y = event.clientY;
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        const touch = event.changedTouches?.[0] || event.touches?.[0];
        if (!touch) return null;
        x = touch.clientX;
        y = touch.clientY;
      }
      if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

      const matches = Array.from(document.querySelectorAll('[data-carturio-wishlist]')).filter((button) => {
        if (!button.isConnected || button.disabled) return false;
        const rect = button.getBoundingClientRect();
        if (rect.width < 2 || rect.height < 2) return false;
        const style = window.getComputedStyle(button);
        if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
        return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
      });
      matches.sort((a, b) => {
        const ra = a.getBoundingClientRect();
        const rb = b.getBoundingClientRect();
        return (ra.width * ra.height) - (rb.width * rb.height);
      });
      return matches[0] || null;
    };

    const activateWishlistButton = (button, event) => {
      const handle = button?.getAttribute('data-carturio-wishlist');
      if (!handle) return false;
      event?.preventDefault();
      event?.stopPropagation();
      if (event && typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      toggleWishlist(handle);
      return true;
    };

    // Pointer Events cover current iOS Safari and Android browsers.
    if ('PointerEvent' in window) {
      document.addEventListener('pointerdown', (event) => {
        const button = findWishlistButtonAtEvent(event);
        if (button) skipCompatibilityClick.delete(button);
        if (button && event.pointerType === 'touch' && !button.disabled) {
          touchStarts.set(event.pointerId, button);
        }
      }, true);

      document.addEventListener('pointercancel', (event) => {
        touchStarts.delete(event.pointerId);
      }, true);

      document.addEventListener('pointerup', (event) => {
        if (event.pointerType !== 'touch') return;
        const started = touchStarts.get(event.pointerId);
        touchStarts.delete(event.pointerId);
        const ended = findWishlistButtonAtEvent(event);
        if (!started || started !== ended || started.disabled || !started.isConnected) return;
        skipCompatibilityClick.add(started);
        activateWishlistButton(started, event);
      }, true);
    } else {
      // Legacy touch fallback for older mobile webviews without Pointer Events.
      let touchStartButton = null;
      document.addEventListener('touchstart', (event) => {
        touchStartButton = findWishlistButtonAtEvent(event);
      }, { capture: true, passive: true });
      document.addEventListener('touchcancel', () => { touchStartButton = null; }, true);
      document.addEventListener('touchend', (event) => {
        const ended = findWishlistButton(event.target);
        const started = touchStartButton;
        touchStartButton = null;
        if (!started || started !== ended || started.disabled || !started.isConnected) return;
        skipCompatibilityClick.add(started);
        activateWishlistButton(started, event);
      }, { capture: true, passive: false });
    }

    // Keyboard, screen reader, mouse and browser-generated clicks use this path.
    document.addEventListener('click', (event) => {
      const clicked = getElement(event.target);
      if (!clicked) return;
      const wish = findWishlistButtonAtEvent(event) || clicked.closest('[data-carturio-wishlist]');
      if (wish) {
        if (event.detail !== 0 && skipCompatibilityClick.has(wish)) {
          skipCompatibilityClick.delete(wish);
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return;
        }
        activateWishlistButton(wish, event);
        return;
      }
      if (clicked.closest('[data-carturio-open-wishlist]')) { event.preventDefault(); openWishlist(); return; }
      if (clicked.closest('[data-carturio-close-wishlist]')) { event.preventDefault(); closeWishlist(); return; }
      const quick = clicked.closest('[data-carturio-quick-view]');
      if (quick) { event.preventDefault(); quickView(quick.getAttribute('data-carturio-quick-view')); return; }
      if (clicked.closest('[data-carturio-quick-view-close]')) closeQuickView();
    }, true);
  };
  installWishlistInteractions();

  document.addEventListener('DOMContentLoaded', () => {
    document.addEventListener('submit', (event) => {
      const form = event.target instanceof Element ? event.target.closest('[data-carturio-quick-add]') : null;
      if (form) { event.preventDefault(); quickAdd(form); }
    });

    window.addEventListener('pageshow', () => document.body.classList.remove('carturio-navigating'));
    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : event.target?.parentElement;
      const link = target?.closest('a');
      if (!link || link.target === '_blank' || link.origin !== window.location.origin || link.hasAttribute('download') || reducedMotion()) return;
      if (link.closest('shopify-account') || link.href.includes('/customer_authentication/') || link.href.includes('/account')) return;
      document.body.classList.add('carturio-navigating');
    });

    const revealFallback = () => document.querySelectorAll('.carturio-reveal').forEach((item) => item.classList.add('is-visible'));
    const initializeSafely = (initializer) => {
      try {
        const pending = initializer();
        if (pending && typeof pending.catch === 'function') pending.catch(revealFallback);
      } catch (_) {
        revealFallback();
      }
    };

    [
      setupSearch,
      setupRecent,
      setupProductRails,
      syncCounts,
      renderWishlistPage,
      setupMobileMenu,
      setupMotion,
      setupScrollProgress,
      setupTracking
    ].forEach(initializeSafely);
  });

  window.Carturio = { wishlist: { all: wishlist, toggle: toggleWishlist }, openWishlist, closeWishlist };
})();
